/**
 * NCC: the free-shipping bar's "unlocked" moment (snippets/ncc-free-shipping-bar.liquid).
 *
 * The bar itself is Liquid: the drawer and the cart page re-render it with the cart, and the `--reached` class
 * arrives through Horizon's morph. This element only notices the MOMENT the threshold is crossed - the class
 * turning up on a bar that was showing "away from" a second ago, or a bar created by a re-render after page
 * load (the add that unlocked it on a page that had no bar) - and plays the one-shot celebration by setting
 * `data-celebrating` for the length of the CSS animations, timed to the fill reaching the badge. A page that
 * loads already unlocked stays still.
 *
 * Loaded from cart-drawer.liquid and main-cart.liquid at the top of their section, never inside the markup a
 * re-render morphs: a script tag that arrives through a section re-render never executes.
 */

const REACHED_CLASS = 'ncc-shipping-bar--reached';
// the attribute goes on right after the morph that brought the class has finished (a synchronous attribute pass
// could still strip it); the CSS delays then time the pop, ring, roll and shine to the fill's 0.4s arrival at the
// badge (Miles, 2026-09-17: the van reacted a beat after the bar had filled)
const MORPH_SETTLE = 0;
const CELEBRATION_LENGTH = 1500; // covers the longest animation + its delay (ncc-theme.css)

let upgradingInitialMarkup = true;

/** @param {string | null} value */
const hasReached = (value) => (value || '').split(/\s+/).includes(REACHED_CLASS);

class ShippingBar extends HTMLElement {
  static observedAttributes = ['class'];

  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    // a bar that did not exist at page load and arrives unlocked was unlocked by the change that created it
    if (!upgradingInitialMarkup && this.classList.contains(REACHED_CLASS)) this.#celebrate();
  }

  disconnectedCallback() {
    this.#settle();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'class' || oldValue === null) return; // null = the element's own attributes on upgrade
    const was = hasReached(oldValue);
    const is = hasReached(newValue);
    if (!was && is) this.#celebrate();
    if (was && !is) this.#settle();
  }

  #celebrate() {
    this.#settle();
    this.#timer = window.setTimeout(() => {
      this.setAttribute('data-celebrating', '');
      this.#timer = window.setTimeout(() => this.#settle(), CELEBRATION_LENGTH);
    }, MORPH_SETTLE);
  }

  #settle() {
    window.clearTimeout(this.#timer);
    this.removeAttribute('data-celebrating');
  }
}

if (!customElements.get('ncc-shipping-bar')) customElements.define('ncc-shipping-bar', ShippingBar);
// define() upgrades every bar already in the document synchronously, so from here on a connect is a re-render
upgradingInitialMarkup = false;
